///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';

import { GtthReactiveInputBase } from './input-base';
import { Observable, Subject } from 'rxjs';
import { GtthReactiveTextbox } from './input-text';
import { GtthReactiveNumber } from './input-number';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable()
export class GtthInputControlService {
    private readonly inputChangeSource = new Subject<GtthReactiveInputBase<any>>();
    public inputChange$ = this.inputChangeSource.asObservable();

    private readonly formSubmitSource = new Subject<FormGroup>();
    public formSubmitSource$ = this.formSubmitSource.asObservable();

    toFormGroup(inputs: GtthReactiveInputBase<any>[]): FormGroup {
        const group = new FormGroup({});

        inputs.forEach((input) => {
            this.parseFormControl(group, input);
        });

        return group;
    }

    public parseFormControl(group, input: GtthReactiveInputBase<any>) {
        if (!input.formGroupName) {
            input.originKey = input.key;
            const formGroupName = input.key.split('.');
            formGroupName.splice(-1, 1);
            input.formGroupName = formGroupName.join('.');
        }

        const inputKey = input.key.split('.');

        if (inputKey.length > 1) {
            const groupKey = inputKey.splice(0, 1)[0];
            if (!group.contains(groupKey)) {
                group.addControl(groupKey, new FormGroup({}));
            }
            input.key = inputKey.join('.');
            this.parseFormControl(group.get(groupKey), input);
        } else {
            group.addControl(
                input.key,
                new FormControl(
                    {
                        value: input.value,
                        disabled: input.disabled
                    },
                    Validators.compose(this.getValidatorArr(input))
                )
            );
        }
    }

    public getInpFormGroup(form: FormGroup, input: GtthReactiveInputBase<any>): AbstractControl {
        if (input?.formGroupName) {
            return form.get(input.formGroupName);
        } else {
            return form;
        }
    }

    public getInputControl(form: FormGroup, input: GtthReactiveInputBase<any>): AbstractControl {
        return form.get(input.originKey);
    }

    public setInputVal(form: FormGroup, input: GtthReactiveInputBase<any>, value: any) {
        const inputControl = this.getInputControl(form, input);
        inputControl.setValue(value);
        inputControl.markAsDirty();
        inputControl.updateValueAndValidity();
    }

    public inputChangeEventBroadcast(input: GtthReactiveInputBase<any>) {
        this.inputChangeSource.next(input);
    }

    public formSubmitEventBroadcast(form: FormGroup) {
        this.formSubmitSource.next(form);
    }

    public updateInputValidation(form: FormGroup, input: GtthReactiveInputBase<any>) {
        const inputControl = this.getInputControl(form, input);
        inputControl.setValidators(this.getValidatorArr(input));
        inputControl.updateValueAndValidity();
    }

    public addSearchField(form: FormGroup, inputKey: string): string {
        const searchInpKey = inputKey + '-search';
        form.addControl(searchInpKey, new FormControl(''));
        return searchInpKey;
    }

    public fieldValueChange(form: FormGroup, input: GtthReactiveInputBase<any>): Observable<any> {
        return this.getInputControl(form, input).valueChanges;
    }

    private getValidatorArr(input: GtthReactiveInputBase<any>): any[] {
        let validationFnArr = [];
        if (input.required) {
            validationFnArr.push(Validators.required);
        }
        if (input instanceof GtthReactiveTextbox || input instanceof GtthReactiveNumber) {
            if (isDefined(input.min)) {
                validationFnArr.push(Validators.min(input.min));
            }
            if (isDefined(input.max)) {
                validationFnArr.push(Validators.max(input.max));
            }
            if (isDefined(input.minLength)) {
                validationFnArr.push(Validators.minLength(input.minLength));
            }
            if (isDefined(input.maxLength)) {
                validationFnArr.push(Validators.maxLength(input.maxLength));
            }
        }
        if (input.validationFn) {
            validationFnArr = validationFnArr.concat(input.validationFn);
        }
        return validationFnArr;
    }
}
